// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Import Material UI
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from '@material-ui/core/CircularProgress';

// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, initialActions, changedFields } from "../../../../recoil-store/recoil-state";

// Import Pages
import { Checkboxes, FileInputMateDrive, RadioButtons, TabPanel, Dropdown } from '../../../FlowConfigurationInputs';
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';
import Modal from '../../../Modal/Modal';
import Info from "../../../../home/pages/automs/autom/editAction/components/Info";

// Import Helpers
import { CheckIfFlowUpdated, isActionValueController } from '../../../../helpers/helper';

// Import Services
import AutomService from "../../../../services/AutomService/AutomService";

// Import Icons
import ResetIcon from '../../../../assets/icons/headerIcons/reset-icon-new.svg';

// Import CSS
import "./ReadFile.css";

function ReadFile({ action, showAlert, setOpenVariable, variables, automId, automVersionId, getVariables }) {
    const { t } = useTranslation();
    const [value, setValue] = useState(0);
    const [sheetsFetched, setSheetsFetched] = useState(false);
    const [sheetsButtonDisabled, setSheetsButtonDisabled] = useState(true);
    const [sheets, setSheets] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [newSheetName, setNewSheetName] = useState('');
    const [sheetsLoading, setSheetsLoading] = useState(true);

    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const label = 'CONTENT.CONFIGURATION-FORM.EXCEL.READ-FILE.';
    const options = {
        fileInput: {
            checkExtension: (ext) => {
                if (ext.toLocaleLowerCase() !== 'xls' && ext.toLocaleLowerCase() !== 'xlsx' && ext.toLocaleLowerCase() !== 'xlsm') {
                    showAlert(t(label + 'UPLOAD_EXCEL_ONLY'));
                    return false;
                }
                return true;
            },
            fileType: '.xlsm, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        radioButtons: {
            data: [{
                value: 'ro',
                label: label + 'READ-ONLY'
            }, {
                value: 'rw',
                label: label + 'READ-WRITE'
            }],
            defaultValue: 'rw'
        },
        checkboxes: { label: label + 'SHEET-CONTAINS-A-HEADER' },
        dropdown: {
            data: [
                {
                    value: "dd-mm-yyyy",
                    label: "DD-MM-YYYY",
                },
                {
                    value: "mm-dd-yyyy",
                    label: "MM-DD-YYYY",
                },
                {
                    value: "yyyy-mm-dd",
                    label: "YYYY-MM-DD",
                },
                {
                    value: "dd.mm.yyyy",
                    label: "DD.MM.YYYY",
                },
                {
                    value: "mm.dd.yyyy",
                    label: "MM.DD.YYYY",
                },
                {
                    value: "yyyy.mm.dd",
                    label: "YYYY.MM.DD",
                },
                {
                    value: "dd/mm/yyyy",
                    label: "DD/MM/YYYY",
                },
                {
                    value: "mm/dd/yyyy",
                    label: "MM/DD/YYYY",
                },
                {
                    value: "yyyy/mm/dd",
                    label: "YYYY/MM/DD",
                },
            ],
            defaultValue: "yyyy.mm.dd",
        }
    }

    useEffect(() => {
        setNewSheetName('');
        setSheetsLoading(true);
        if (!action.fields.fileType) {
            action.fields.fileType = { value: "FILE" };

        } else {
            if (action.fields.fileType.value === "FILE") {
                setValue(0);
            } else {
                setValue(1)
            }
        }

        if (!action.fields?.sheetsFetched) {
            action.fields.sheetsFetched = { value: '', isRequired: true };
        }

        if (!action.fields?.sheets) {
            action.fields.sheets = { value: [] };
            setSheets([]);
        } else {
            setSheets(action.fields.sheets.value ? action.fields.sheets.value : []);
        }
        if (action.fields?.sheetsFetched?.value) {
            setSheetsFetched(true);
        } else {
            setSheetsFetched(false);
        }

        if (action.fields?.filePath?.value) {
            setSheetsButtonDisabled(false);
        } else {
            setSheetsButtonDisabled(true);
        }
        setTimeout(() => {
            setSheetsLoading(false)
        }, 500)

    }, [action.id])

    const resetSheets = (checkDisabled) => {
        for (const key of Object.keys(action.fields)) {
            if (key.includes('headerContains') || key.includes('set')) {
                delete action.fields[key];
            }
        }
        action.fields.sheetsFetched.value = '';
        action.fields.sheets.value = [];
        setSheetsFetched(false);
        setSheets([]);
        if (checkDisabled) {
            if (!action.fields.filePath.value) {
                setSheetsButtonDisabled(true);
                return false;
            } else {
                setSheetsButtonDisabled(false);
                return true;
            }
        }
    }

    function handleChangeTabs(newValue) {
        if (newValue === 1) {
            // action.fields.filePath.type = 'TEXT';
            action.fields.fileType.value = 'TEXT';
            action.fields.filePath.backup = { value: action.fields.filePath.value };
            action.fields.filePath.value = "";
            if (action.fields.filePath.backupText?.value) {
                action.fields.filePath.value = action.fields.filePath.backupText.value;
            }
        } else {
            // action.fields.filePath.type = 'FILE';
            action.fields.fileType.value = 'FILE';

            action.fields.filePath.backupText = { value: action.fields.filePath.value }
            delete action.fields.filePath.value
            if (action.fields.filePath.backup?.value) {
                action.fields.filePath.value = action.fields.filePath.backup.value;
            }
        }
        const field = initialActionsData[action.id]?.fields.fileType;
        const oldValue = !field ? undefined : field.value;
        let v = newValue === 1 ? 'TEXT' : 'FILE';
        CheckIfFlowUpdated("fileType", action, oldValue, v, setSaveAvailable, setChangeFields, changedFieldsState);
        setValue(newValue);

        resetSheets(true);
    }

    const handleClick = async () => {
        let res = isActionValueController(action.fields);
        if (res.indexOf(false) === -1) {
            action.automId = automId;
            action.automVersionId = automVersionId;
            let response = await AutomService.runAction(action);
            if (response) {
                if (response.status) {
                    showAlert(t('MESSAGES.AUTOMS.ACTION-RUN-SUCCESSFULLY'), true);
                } else {
                    showAlert(t('FLOW-MESSAGES.LIBRARY.' + response.message), false);
                }
            } else {
                showAlert(t('GENERAL.NETWORK-ERROR'), false);
            }
            getVariables(automVersionId)
        } else {
            showAlert(t('ACTIVE-DIRECTORY.NOT-NULL'), false);
        }
    }

    const onBlurFilePath = (v) => {
        if (v) {
            setSheetsButtonDisabled(false);
        } else {
            setSheetsButtonDisabled(true);
        }
        const field = initialActionsData[action.id]?.fields?.filePath;
        const oldValue = !field ? undefined : field.value;
        if (oldValue !== action.fields.filePath.value) {
            resetSheets(false)
        }
    }

    const getSheets = async () => {
        setSheetsLoading(true);
        if (!resetSheets(true)) {
            showAlert(t('FLOW-MESSAGES.LIBRARY.EXCEL.FILE-PATH-NOT-FOUND'), false);
            setSheetsLoading(false);
            return;
        }

        const data = {
            automId,
            automVersionId,
            libraryName: 'EXCEL',
            actionName: 'READ-SHEETS',
            action_name: 'READ-SHEETS',
            action_label: 'READSHEETS',
            name: 'READ-SHEETS',
            fields: {
                filePath: { value: action.fields.filePath.value },
                fileType: { value: action.fields.fileType.value }
            }
        }
        let response = await AutomService.runAction(data);
        if (response) {
            if (response.status) {
                try {
                    let s = JSON.parse(response.dataString)
                    if (s && s.length) {

                        action.fields.sheets = { value: s }
                        setSheets(s);
                        for (const sheet_name of s) {
                            action.fields[`${sheet_name}_headerContains`] = { value: '' }
                        }
                        action.fields.sheetsFetched.value = 'fetched';
                        setSheetsFetched(true);

                        showAlert(t('FLOW-MESSAGES.LIBRARY.EXCEL.SHEETS-NAMES-FETCHED-SUCCESSFULLY'), true);

                        const field = initialActionsData[action.id]?.fields.sheets;
                        const oldValue = !field ? undefined : field.value;
                        CheckIfFlowUpdated("sheets", action, oldValue, s, setSaveAvailable, setChangeFields, changedFieldsState);
                    }
                } catch (error) {
                    //     
                }
            } else {
                showAlert(t('FLOW-MESSAGES.LIBRARY.' + response.message), false);
            }
        } else {
            showAlert(t('GENERAL.NETWORK-ERROR'), false);
        }
        setSheetsLoading(false);
    }

    const addManualSheet = () => {
        if (newSheetName === "") {
            showAlert(t('GENERAL.NOT-NULL'), false);
            return;
        } else if (sheets.includes(newSheetName)) {
            showAlert(t(label + 'SHEET-NAME-ALREADY-EXISTS'), false);
            return;
        }
        let s = [...sheets, newSheetName];
        action.fields.sheets = { value: s };
        action.fields.sheetsFetched.value = 'fetched';
        setNewSheetName('');
        setSheets(s);
        setSheetsFetched(true);
        setOpenModal(false);

        const field = initialActionsData[action.id]?.fields.sheets;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("sheets", action, oldValue, s, setSaveAvailable, setChangeFields, changedFieldsState);
    }

    const openAddSheetModal = () => {
        if (!action.fields?.filePath.value) {
            resetSheets(false)
            setSheetsButtonDisabled(true);
            showAlert(t('FLOW-MESSAGES.LIBRARY.EXCEL.FILE-PATH-NOT-FOUND'), false);
            return;
        }
        setOpenModal(true)
    }

    return (
        <div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'FILE-PATH')} is_required />
                <div className="condition-tabs" style={{ marginBottom: 5 }}>
                    <button
                        className={`f-s-11 ${value === 0 ? 'condition-active' : ''}`}
                        onClick={() => handleChangeTabs(0)}>
                        {t(label + 'SELECT-FILE')}
                    </button>
                    <button
                        className={`f-s-11 ${value === 1 ? 'condition-active' : ''}`}
                        onClick={() => handleChangeTabs(1)}>
                        {t(label + 'CUSTOM-STRING')}
                    </button>
                </div>
                <TabPanel value={value} index={0}>
                    <FileInputMateDrive action={action} inputName="filePath" options={options.fileInput} isRequired
                        isSize={true} clear={true} hideRequired autom_id={automId} onChangeFile={() => resetSheets(true)} />
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <TextInput action={action} inputName="filePath" isRequired={false} setOpenVariable={setOpenVariable}
                        variables={variables} autom_id={automId} onBlur={(e) => onBlurFilePath(e.target.outerText?.trim())} submit={onBlurFilePath} />
                </TabPanel>
            </div>
            <div className="conf-field-container">
                <FieldLabel is_required />
                <div className='items-center-between'>
                    <Button
                        onClick={getSheets}
                        className={`get-sheets-button ${!sheetsButtonDisabled ? (sheetsFetched ? 'active' : 'pasif') : ''}`}
                        variant="contained"
                        disabled={sheetsButtonDisabled}
                    >
                        {t(label + "GET-SHEETS")}
                    </Button>
                    <Button
                        onClick={openAddSheetModal}
                        className={`get-sheets-button ${!sheetsButtonDisabled ? 'manual' : ''}`}
                        variant="contained"
                        disabled={sheetsButtonDisabled}
                    >
                        {t(label + "ADD-MANUAL-SHEET")}
                    </Button>
                </div>

            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'OPEN-AS')} />
                <RadioButtons action={action} inputName="openAs" options={options.radioButtons} isRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + "DATE-FORMAT")} />
                <div style={{ display: "flex", justifyContent: "space-between", width: "90%" }}>
                    <Dropdown
                        action={action}
                        inputName="dateFormat"
                        options={options.dropdown}
                        isRequired={true}
                    />
                </div>
                <small className='conf-field-desc'><em>{t(label + 'DATE-FORMAT-COMMENT')}</em></small>
            </div>

            <div className="conf-field-container">
                <FieldLabel name={t(label + 'SHEETS')} />
                <div className="attachment-container p-relative">

                    {sheetsLoading ? <CircularProgress className='loading-circular-process' />
                        :
                        sheets.length < 1 ? <div className="conf-field-desc">{t(label + 'SHEETS-NOT-FETCHED')}</div> :
                            <>
                                <button className='sheet-reset-button' onClick={() => resetSheets(true)}>
                                    <Info value={t("CONTENT.FLOW.RESET")} placement="bottom">
                                        <img className='s-width-40' src={ResetIcon} alt="" />
                                    </Info>
                                </button>
                                {
                                    sheets.map((sheet_name, itemI) =>
                                        <div key={itemI} style={{ marginBottom: 10 }}>
                                            <div className="conf-field-container">
                                                <Checkboxes action={action} inputName={`${sheet_name}_headerContains`} options={{ label: `${t(label + 'SHEET-CONTAINS-A-HEADER')} ${sheet_name}` }} />
                                            </div>
                                            <div className="conf-field-container">
                                                <FieldLabel name={sheet_name} />
                                                <VariablePicker action={action} inputName={`${sheet_name}_set`} variables={variables} setOpenVariable={setOpenVariable}
                                                    filter="data table" />
                                                <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'data table' })}</em></small>
                                            </div>
                                        </div>
                                    )
                                }
                            </>
                    }
                </div>

            </div>

            {/* <div className="conf-field-container">
                <Checkboxes action={action} inputName="headerContains" options={options.checkboxes} />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'ASSIGN')} is_required />
                <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable}
                    filter="data table" isRequired hideRequired />
                <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'data table' })}</em></small>
            </div> */}
            <button disabled={sheetsFetched ? false : true} style={{ opacity: sheetsFetched ? 1 : 0.5 }} className="form-button" onClick={() => handleClick()}>Run Once</button>
            {
                openModal &&
                <Modal title={t(label + "ADD-MANUAL-SHEET")} setOpen={() => { setOpenModal(false); setNewSheetName(''); }}>
                    <div className='conf-field-container add-sheet-container'>
                        <FieldLabel name={t(label + "SHEET-NAME")} is_required/>
                        <div className='items-center-between'>
                            <TextField value={newSheetName} onChange={(e) => setNewSheetName(e.target.value)} variant="outlined" />
                            <Button
                                onClick={() => addManualSheet()}
                                className={`get-sheets-button submit-sheet`}
                                variant="contained"
                            >
                                {t(label + "ADD-SHEET")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            }
        </div>
    )
}

export default ReadFile;
